import { useState } from "react";
import { Button } from "./Button";
import type { Issue, IssueActsApi } from "../lib/tasks";
import { errText, handToNaru, issueActsApi } from "../lib/tasks";
import { tx } from "../i18n";

/* ══ 보드 선택 줄 ══
   채택 시안 Figma 31:2 ③. 카드를 하나라도 고르면 보드 바닥에 선다. **새 서버 문을 안 만든다** —
   상세의 단추가 부르는 같은 문을 고른 카드마다 부르고, 결과를 「N건 됨 · M건 못 함(이유)」로
   한 줄 적는다. 전부-아니면-전무가 아니다: 하나가 거절돼도 나머지는 간다. */

type Act = "ready" | "naru" | "unrequest" | "delete";

export function BoardSelBar({
  picked,
  api = issueActsApi,
  onDone,
  onClear,
  initialConfirm = false,
}: {
  picked: Issue[];
  api?: IssueActsApi;
  /** 한 번 다 돌고 나면 부른다 — 보드가 다시 읽는다. 됐던 것은 고른 목록에서 빠진다. */
  onDone: (okIds: string[]) => void;
  onClear: () => void;
  /** 패턴 페이지가 삭제 확인을 펼친 채로 보이려고 쓴다. */
  initialConfirm?: boolean;
}) {
  const [busy, setBusy] = useState<Act | null>(null);
  const [confirm, setConfirm] = useState(initialConfirm);
  const [result, setResult] = useState<string | null>(null);

  async function each(act: Act, f: (t: Issue) => Promise<unknown>) {
    setBusy(act);
    setResult(null);
    const ok: string[] = [];
    const why = new Map<string, number>();
    for (const t of picked) {
      try {
        await f(t);
        ok.push(t.id);
      } catch (e) {
        // 같은 거절은 한 번만 적는다 — 열 건이 같은 이유로 막혔으면 열 줄이 아니라 「×10」이다.
        const k = errText(e);
        why.set(k, (why.get(k) ?? 0) + 1);
      }
    }
    const fail = picked.length - ok.length;
    setResult(
      fail === 0
        ? tx("boardSel.result", { ok: ok.length })
        : tx("boardSel.resultFail", {
            ok: ok.length,
            fail,
            why: [...why].map(([k, n]) => (n > 1 ? `${k} ×${n}` : k)).join(" / "),
          }),
    );
    setBusy(null);
    setConfirm(false);
    onDone(ok);
  }

  if (picked.length === 0 && !result) return null;
  return (
    <div className="b-bulkbar" role="region" aria-label={tx("boardSel.count", { n: picked.length })}>
      <div className="b-bulkbar-row">
        <strong>{tx("boardSel.count", { n: picked.length })}</strong>
        {confirm ? (
          <>
            <span className="b-bulkbar-note">{tx("boardSel.deleteAsk", { n: picked.length })}</span>
            <Button size="sm" onClick={() => setConfirm(false)} disabled={busy !== null}>
              {tx("common.action.close")}
            </Button>
            <Button
              size="sm"
              variant="danger"
              loading={busy === "delete"}
              onClick={() => each("delete", (t) => api.remove(t.id, false))}
            >
              {tx("boardSel.deleteGo")}
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              loading={busy === "ready"}
              disabled={busy !== null || picked.length === 0}
              onClick={() => each("ready", (t) => api.setStatus(t.id, "ready"))}
            >
              {tx("boardSel.toReady")}
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={busy === "naru"}
              disabled={busy !== null || picked.length === 0}
              onClick={() => each("naru", (t) => handToNaru(api, t))}
            >
              → {tx("boardSel.toNaru")}
            </Button>
            <Button
              size="sm"
              loading={busy === "unrequest"}
              disabled={busy !== null || picked.length === 0}
              onClick={() => each("unrequest", (t) => api.agentRequest(t.id, false))}
            >
              {tx("boardSel.unrequest")}
            </Button>
            <Button size="sm" variant="danger" disabled={busy !== null || picked.length === 0} onClick={() => setConfirm(true)}>
              {tx("common.action.delete")}
            </Button>
            <span className="b-bulkbar-gap" />
            <Button
              size="sm"
              variant="ghost"
              disabled={busy !== null}
              onClick={() => {
                setResult(null);
                onClear();
              }}
            >
              {tx("boardSel.clear")}
            </Button>
          </>
        )}
      </div>
      {result ? <p className="b-bulkbar-result">{result}</p> : null}
    </div>
  );
}
